import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { toast } from "sonner";
import {
    Clock, Plus, Download, CheckCircle2, AlertCircle, Loader2, Mail,
    HandHeart, ShieldCheck, ArrowRight,
} from "lucide-react";
import { API } from "../lib/api";
import { formatApiError } from "../lib/errors";

/**
 * VolunteerPortal — magic-link volunteer dashboard.
 *
 * If a `?token=` is present in the URL, we call GET /volunteer-portal/me
 * with the token in the X-Volunteer-Token header, and render the volunteer's
 * hours dashboard. If no valid token, we render a "request a link" form.
 *
 * This is the OPTIONAL account upgrade for volunteers — no password required.
 */
const VolunteerPortal = () => {
    const [sp] = useSearchParams();
    const token = sp.get("token") || "";

    if (!token) return <RequestLinkView />;
    return <DashboardView token={token} />;
};

// ------------------------------------------------------------
// No-token: request a magic link by email
// ------------------------------------------------------------
const RequestLinkView = () => {
    const [email, setEmail] = useState("");
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/volunteer-portal/request`, { email });
            setSent(true);
        } catch (err) {
            toast.error(formatApiError(err) || "Couldn't send link");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-80px)] bg-[#FDFBF7] py-24 px-6" data-testid="vol-portal-request-page">
            <Helmet><title>Volunteer dashboard · Reddog Foundation</title></Helmet>
            <div className="max-w-lg mx-auto">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-11 h-11 rounded-full bg-[#F2D8D5] flex items-center justify-center">
                        <HandHeart className="w-5 h-5 text-[#E53935]" strokeWidth={1.5} />
                    </div>
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.18em] text-[#E53935] font-bold">Volunteer dashboard</p>
                        <h1 className="font-heading text-3xl text-[#1C252E] font-medium">Track your service hours</h1>
                    </div>
                </div>

                {sent ? (
                    <div className="bg-white border border-[#E53935]/30 rounded-2xl p-8" data-testid="vol-portal-request-sent">
                        <div className="w-12 h-12 rounded-full bg-[#F2D8D5] flex items-center justify-center mb-4">
                            <Mail className="w-6 h-6 text-[#E53935]" strokeWidth={1.5} />
                        </div>
                        <p className="font-heading text-xl text-[#1C252E] mb-2">Check your inbox</p>
                        <p className="text-sm text-[#4A5568] leading-relaxed">
                            If a volunteer signup matches that email, we just sent a dashboard link. It&apos;s valid for 14 days and doesn&apos;t require a password.
                        </p>
                        <p className="text-xs text-[#4A5568] mt-4">
                            Not signed up yet? <Link to="/about#volunteer" className="text-[#E53935] underline">Volunteer with us first.</Link>
                        </p>
                    </div>
                ) : (
                    <form onSubmit={submit} className="bg-white border border-black/5 rounded-2xl p-8 space-y-5" data-testid="vol-portal-request-form">
                        <p className="text-sm text-[#4A5568] leading-relaxed">
                            Enter the email you signed up with. We&apos;ll email you a private, one-click link so you can log your hours and download a service-hour certificate — no password required.
                        </p>
                        <div>
                            <label className="text-xs uppercase tracking-wider font-bold text-[#1C252E] block mb-1.5" htmlFor="vol-portal-email">Your email</label>
                            <input
                                id="vol-portal-email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="email"
                                data-testid="vol-portal-request-email"
                                className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={sending}
                            data-testid="vol-portal-request-btn"
                            className="w-full bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full py-3 font-medium transition-all disabled:opacity-60"
                        >
                            {sending ? "Sending…" : "Email me a dashboard link"}
                        </button>
                        <p className="text-[11px] text-[#4A5568] flex items-start gap-1.5 leading-relaxed">
                            <ShieldCheck className="w-3.5 h-3.5 text-[#4A5568] flex-shrink-0 mt-0.5" strokeWidth={2} />
                            We never reveal whether an email is on file. The link is only sent when a matching volunteer signup exists.
                        </p>
                    </form>
                )}
            </div>
        </div>
    );
};

// ------------------------------------------------------------
// With-token: dashboard view
// ------------------------------------------------------------
const DashboardView = ({ token }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [logging, setLogging] = useState(false);
    const [form, setForm] = useState({
        hours: "",
        date: new Date().toISOString().slice(0, 10),
        activity: "",
        notes: "",
    });

    const hdr = { headers: { "X-Volunteer-Token": token } };

    const load = () => {
        setLoading(true);
        axios.get(`${API}/volunteer-portal/me`, hdr)
            .then((r) => setData(r.data))
            .catch((err) => setError(formatApiError(err) || "Couldn't open dashboard"))
            .finally(() => setLoading(false));
    };

     
    useEffect(() => { load(); }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        setLogging(true);
        try {
            await axios.post(`${API}/volunteer-portal/hours`, {
                hours: parseFloat(form.hours),
                date: form.date,
                activity: form.activity,
                notes: form.notes,
            }, hdr);
            toast.success(`Logged ${form.hours} hours — pending board verification`);
            setForm({ ...form, hours: "", activity: "", notes: "" });
            load();
        } catch (err) {
            toast.error(formatApiError(err) || "Couldn't log hours");
        } finally {
            setLogging(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center" data-testid="vol-portal-loading">
                <Loader2 className="w-6 h-6 animate-spin text-[#E53935]" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-[calc(100vh-80px)] bg-[#FDFBF7] py-24 px-6">
                <div className="max-w-lg mx-auto bg-white border border-red-200 rounded-2xl p-8 text-center" data-testid="vol-portal-error">
                    <AlertCircle className="w-8 h-8 text-red-600 mx-auto mb-3" strokeWidth={1.5} />
                    <p className="font-heading text-xl text-[#1C252E] mb-2">Link expired or invalid</p>
                    <p className="text-sm text-[#4A5568] mb-6">{error}</p>
                    <Link to="/volunteer-portal" className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-6 py-2.5 text-sm font-medium">
                        Request a new link <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        );
    }

    const total = data?.total_hours || 0;
    const verified = data?.verified_hours || 0;
    const entries = data?.entries || [];
    const inKindValue = data?.in_kind_value || 0;

    return (
        <div className="min-h-[calc(100vh-80px)] bg-[#FDFBF7] py-16 px-6" data-testid="vol-portal-dashboard">
            <Helmet><title>Volunteer dashboard · Reddog Foundation</title></Helmet>
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#E53935] font-bold mb-2">Volunteer dashboard</p>
                    <h1 className="font-heading text-4xl sm:text-5xl text-[#1C252E] font-medium leading-tight" data-testid="vol-portal-greeting">
                        Hi {data?.name || "there"} —
                    </h1>
                    <p className="text-sm text-[#4A5568] mt-3 max-w-2xl">
                        Log hours as you go. Board-verified entries count toward your official service-hour certificate. Bookmark this link — it&apos;s valid for {data?.token_ttl_days || 14} days, then just request a new one.
                    </p>
                </div>

                {/* Stat cards */}
                <div className="grid sm:grid-cols-3 gap-4 mb-8">
                    <StatCard label="Total logged" value={`${total.toFixed(1)} hrs`} testid="vol-portal-total-hours" />
                    <StatCard label="Board-verified" value={`${verified.toFixed(1)} hrs`} accent testid="vol-portal-verified-hours" />
                    <StatCard label="In-kind value" value={`$${inKindValue.toFixed(0)}`} testid="vol-portal-inkind-value" />
                </div>

                {/* Certificate button */}
                <div className="mb-8 flex flex-wrap gap-3">
                    {total > 0 ? (
                        <a
                            href={`${API}/volunteer-portal/certificate.pdf?token=${encodeURIComponent(token)}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="vol-portal-certificate-btn"
                            className="inline-flex items-center gap-2 bg-[#1C252E] text-white hover:bg-black rounded-full px-5 py-2.5 text-sm font-medium transition-all"
                        >
                            <Download className="w-4 h-4" strokeWidth={1.5} />
                            Download service-hour certificate
                        </a>
                    ) : (
                        <p className="text-sm text-[#4A5568]">Log your first hour below to unlock your certificate.</p>
                    )}
                </div>

                {/* Log form */}
                <div className="bg-white border border-black/5 rounded-2xl p-6 mb-8">
                    <div className="flex items-center gap-2 mb-4">
                        <Clock className="w-4 h-4 text-[#E53935]" strokeWidth={2} />
                        <h2 className="font-heading text-lg text-[#1C252E] font-medium">Log new hours</h2>
                    </div>
                    <form onSubmit={submit} className="grid sm:grid-cols-4 gap-2" data-testid="vol-portal-log-form">
                        <input
                            type="number"
                            step="0.25"
                            min="0.25"
                            max="24"
                            required
                            placeholder="Hours"
                            value={form.hours}
                            onChange={(e) => setForm({ ...form, hours: e.target.value })}
                            data-testid="vol-portal-hours-input"
                            aria-label="Hours"
                            className="rounded-lg border border-black/10 px-3 py-2 bg-white text-sm"
                        />
                        <input
                            type="date"
                            required
                            value={form.date}
                            onChange={(e) => setForm({ ...form, date: e.target.value })}
                            data-testid="vol-portal-date-input"
                            aria-label="Date"
                            className="rounded-lg border border-black/10 px-3 py-2 bg-white text-sm"
                        />
                        <input
                            type="text"
                            required
                            placeholder="Activity (e.g. PTA cleanup)"
                            maxLength={200}
                            value={form.activity}
                            onChange={(e) => setForm({ ...form, activity: e.target.value })}
                            data-testid="vol-portal-activity-input"
                            aria-label="Activity"
                            className="rounded-lg border border-black/10 px-3 py-2 bg-white text-sm"
                        />
                        <button
                            type="submit"
                            disabled={logging}
                            data-testid="vol-portal-log-btn"
                            className="inline-flex items-center justify-center gap-1.5 bg-[#E53935] hover:bg-[#D32F2F] disabled:opacity-60 text-white rounded-lg px-4 py-2 text-sm font-medium"
                        >
                            {logging ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                            Log
                        </button>
                    </form>
                </div>

                {/* Entries */}
                <div className="bg-white border border-black/5 rounded-2xl p-6">
                    <h2 className="font-heading text-lg text-[#1C252E] font-medium mb-4">Recent entries</h2>
                    {entries.length === 0 ? (
                        <p className="text-sm text-[#4A5568]" data-testid="vol-portal-empty">
                            No hours logged yet. Add your first entry above.
                        </p>
                    ) : (
                        <ul className="divide-y divide-black/5" data-testid="vol-portal-entries">
                            {entries.slice(0, 25).map((e) => (
                                <li key={e.id} className="flex items-center justify-between py-3 text-sm">
                                    <div className="flex items-center gap-3 min-w-0">
                                        {e.status === "verified" ? (
                                            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" strokeWidth={2} />
                                        ) : (
                                            <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" strokeWidth={2} />
                                        )}
                                        <div className="min-w-0">
                                            <p className="text-[#1C252E] truncate">{e.activity}</p>
                                            <p className="text-xs text-[#4A5568]">{e.date} · {e.status === "verified" ? "verified" : "pending verification"}</p>
                                        </div>
                                    </div>
                                    <p className="font-medium text-[#1C252E] flex-shrink-0">{parseFloat(e.hours || 0).toFixed(1)} hrs</p>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

const StatCard = ({ label, value, accent = false, testid }) => (
    <div className="bg-white border border-black/5 rounded-2xl p-5">
        <p className="text-xs uppercase tracking-wider text-[#4A5568] font-bold">{label}</p>
        <p
            className={`font-heading text-3xl font-medium mt-1 ${accent ? "text-[#E53935]" : "text-[#1C252E]"}`}
            data-testid={testid}
        >
            {value}
        </p>
    </div>
);

export default VolunteerPortal;
